import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Flame, Waves, Boxes, ClipboardCheck } from "lucide-react";
import { EngineeringGrid } from "@/components/fina/EngineeringGrid";

export const InMotion = () => {
  const ref = useRef(null);

  return (
    <section
      ref={ref}
      data-testid="inmotion-section"
      className="relative bg-[#09090b] overflow-hidden py-32 md:py-48 ambient-depth"
    >
      <EngineeringGrid gridOpacity={0.05} withScanline />

      {/* Editorial copy on top */}
      <div className="relative max-w-6xl mx-auto px-6 md:px-12 text-center">
        <p className="overline mb-6 inline-block" data-testid="inmotion-overline">
          — Our workshops, your advantage
        </p>
        <h2
          className="font-heading text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.03em] max-w-4xl mx-auto"
          data-testid="inmotion-headline"
        >
          Built in stainless.
          <br />
          <span className="italic font-normal text-[#FF5722]">
            Engineered for you.
          </span>
        </h2>
        <p className="mt-8 text-white/55 font-body text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
          Two specialist workshops in Lincolnshire. One focus — precision
          stainless fabrication for demanding food and pharmaceutical
          environments.
        </p>

        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-8 max-w-5xl mx-auto">
          {[
            {
              icon: Flame,
              title: "TIG Welding",
              body: "Coded TIG welders delivering clean, hygienic, crevice-free stainless joints.",
            },
            {
              icon: Waves,
              title: "Pipework Fabrication",
              body: "Hygienic stainless pipework built for pharmaceutical and food-grade systems.",
            },
            {
              icon: Boxes,
              title: "Stainless Fabrication",
              body: "Vessels, skids, platforms and bespoke stainless assemblies, built to spec.",
            },
            {
              icon: ClipboardCheck,
              title: "Quality & Traceability",
              body: "Full material traceability and controlled processes across every project.",
            },
          ].map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="relative flex flex-col items-center text-center px-2"
              data-testid={`inmotion-stat-${i}`}
            >
              <div className="relative mb-5">
                <div
                  aria-hidden
                  className="absolute inset-0 -m-2 rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(255,87,34,0.18), transparent 70%)",
                    filter: "blur(8px)",
                  }}
                />
                <c.icon
                  size={36}
                  strokeWidth={1.4}
                  className="relative text-white"
                />
              </div>
              <div className="font-heading text-white text-lg md:text-xl tracking-tight">
                {c.title}
              </div>
              <p className="mt-3 text-white/55 text-sm leading-relaxed font-body max-w-[220px]">
                {c.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
